/**
 * +castux: WoW-style UI error line ("Out of range", "Not ready yet"...): one short red line at
 * upper centre, just under the target frame. A repeat of the line on screen only extends it (no
 * flicker, no new sound); the soft error sound (audio recipe `ui.error`, through the combat audio
 * settings) is rate-limited. Gentle warnings ("Maelstrom capped", "Low mana") use the same line in
 * amber with no sound.
 *
 * `UiErrorModel` is pure (unit-tested); `UiErrorLine` is the DOM view.
 */
import { combatAudio } from '../audio/engine';
import { el } from './dom';

export type UiErrorTone = 'error' | 'warn';

/** How long a line stays up after its latest show (ms). */
export const ERROR_SHOW_MS = 1700;
/** Gentle warnings stay a little longer (they are rarer and read once). */
export const WARN_SHOW_MS = 2600;
/** The error sound plays at most this often (ms), whatever the text. */
export const ERROR_SOUND_GAP_MS = 650;
/** The same text shown again within this long of its last show does not replay the sound. */
export const SAME_TEXT_QUIET_MS = 1200;

export interface UiErrorShow {
  /** The text / tone on screen changed (restart the entry animation). */
  changed: boolean;
  /** Play the error sound. */
  sound: boolean;
}

export class UiErrorModel {
  text = '';
  tone: UiErrorTone = 'error';
  until = 0;
  private lastSoundAt = -Infinity;
  private lastTextAt = -Infinity;
  private readonly out: UiErrorShow = { changed: false, sound: false };

  /** Show `text` at `now` (ms). Returns what the view should do (reused object). */
  show(text: string, now: number, tone: UiErrorTone = 'error'): UiErrorShow {
    const o = this.out;
    const same = text === this.text && tone === this.tone;
    const visible = now < this.until;
    o.changed = !(same && visible);
    // Mashing a key on cooldown: one sound, the line just stays up.
    const recentSame = same && now - this.lastTextAt < SAME_TEXT_QUIET_MS;
    o.sound = tone === 'error' && !recentSame && now - this.lastSoundAt >= ERROR_SOUND_GAP_MS;
    if (o.sound) this.lastSoundAt = now;
    this.text = text;
    this.tone = tone;
    this.lastTextAt = now;
    this.until = now + (tone === 'warn' ? WARN_SHOW_MS : ERROR_SHOW_MS);
    return o;
  }

  visible(now: number): boolean {
    return now < this.until;
  }
}

export class UiErrorLine {
  readonly root: HTMLElement;
  private readonly model = new UiErrorModel();
  private shown = false;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'error-text hk-ui-error', parent);
    this.root.setAttribute('role', 'status');
  }

  show(text: string, now: number, tone: UiErrorTone = 'error'): void {
    const r = this.model.show(text, now, tone);
    if (r.changed) {
      this.root.textContent = text;
      this.root.classList.toggle('warn', tone === 'warn');
      // Restart the short entry pop (compositor-only).
      this.root.animate?.([{ transform: 'translateX(-50%) scale(1.08)', opacity: 0.4 }, { transform: 'translateX(-50%) scale(1)', opacity: 1 }], { duration: 140, easing: 'ease-out' });
    }
    if (r.sound) combatAudio.play('ui.error', { gain: 0.8, prioBoost: 2 });
  }

  update(now: number): void {
    const on = this.model.visible(now);
    if (on !== this.shown) {
      this.shown = on;
      this.root.classList.toggle('show', on);
    }
  }
}
